// ============================================================================
// appointmentsStorage — CRUD da agenda (Supabase REST)
// ============================================================================
// Substitui o getAppointments/upsertAppointment/deleteAppointment do v2 que
// usava localStorage. REST direto, sem cache local.
// ============================================================================

import { SUPABASE_URL, restHeaders, restFetchAll } from './secureRest.js';

// ─── Conversão snake_case (DB) ↔ camelCase (app) ────────────────────────────

function toApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    clientId: row.client_id || '',
    clientName: row.client_name || '',
    clientPhone: row.client_phone || '',
    professionalId: row.professional_id || '',
    unitId: row.unit_id || '',
    serviceId: row.service_id || '',
    serviceName: row.service_name || '',
    services: row.services || [],
    date: row.date || '',
    startTime: row.start_time || '',
    endTime: row.end_time || '',
    duration: row.duration || 30,
    price: Number(row.price) || 0,
    status: row.status || 'agendado',
    type: row.type || null,
    // isBlock: usa a coluna `type` como marcador (type='block' = bloqueio
    // de horário do profissional, não um agendamento real).
    isBlock: row.type === 'block',
    forcarEncaixe: !!row.forcar_encaixe,
    recurrence: row.recurrence || 'none',
    notes: row.notes || '',
    observacoes: row.observacoes || '',
    createdAt: row.created_at,
    createdBy: row.created_by || '',
    updatedAt: row.updated_at,
    updatedBy: row.updated_by || '',
    lastAccessAt: row.last_access_at || null,
    lastAccessBy: row.last_access_by || '',
  };
}

// UUID v4-ish: 8-4-4-4-12 hex. Aceita lowercase ou maiúsculo.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUuidOrNull = (v) => (typeof v === 'string' && UUID_RE.test(v.trim()) ? v.trim() : null);

function toDb(apt) {
  const row = {};
  if (apt.clientId !== undefined) row.client_id = asUuidOrNull(apt.clientId);
  if (apt.clientName !== undefined) row.client_name = apt.clientName || null;
  if (apt.clientPhone !== undefined) row.client_phone = apt.clientPhone || null;
  if (apt.professionalId !== undefined) row.professional_id = apt.professionalId || null;
  if (apt.unitId !== undefined) row.unit_id = apt.unitId || null;
  if (apt.serviceId !== undefined) row.service_id = apt.serviceId || null;
  if (apt.serviceName !== undefined) row.service_name = apt.serviceName || null;
  if (apt.services !== undefined) row.services = apt.services || [];
  if (apt.date !== undefined) row.date = apt.date;
  if (apt.startTime !== undefined) row.start_time = apt.startTime;
  if (apt.endTime !== undefined) row.end_time = apt.endTime || null;
  if (apt.duration !== undefined) row.duration = Number(apt.duration) || 30;
  if (apt.price !== undefined) row.price = Number(apt.price) || 0;
  if (apt.status !== undefined) row.status = apt.status || 'agendado';
  // isBlock vira type='block' no DB; senão usa o type explícito (se existir).
  if (apt.isBlock === true) row.type = 'block';
  else if (apt.type !== undefined) row.type = apt.type || null;
  if (apt.forcarEncaixe !== undefined) row.forcar_encaixe = !!apt.forcarEncaixe;
  if (apt.recurrence !== undefined) row.recurrence = apt.recurrence || 'none';
  if (apt.notes !== undefined) row.notes = apt.notes || null;
  if (apt.observacoes !== undefined) row.observacoes = apt.observacoes || null;
  if (apt.createdBy !== undefined) row.created_by = apt.createdBy || null;
  if (apt.updatedBy !== undefined) row.updated_by = apt.updatedBy || null;
  if (apt.lastAccessAt !== undefined) row.last_access_at = apt.lastAccessAt || null;
  if (apt.lastAccessBy !== undefined) row.last_access_by = apt.lastAccessBy || null;
  return row;
}

// ─── CRUD ───────────────────────────────────────────────────────────────────

// Lista agendamentos. Por padrão pega últimos 60 dias + próximos 90.
// Use opts={startDate, endDate} pra range custom.
export async function listAppointments(opts = {}) {
  const { startDate, endDate, unitId, professionalId, limit = 5000 } = opts;
  // REGRA LINEAR (15/07/2026): paginado até o fim. O PostgREST corta em 1000
  // SILENCIOSAMENTE, e com order=date.asc os cortados eram exatamente os
  // agendamentos FUTUROS (a agenda de amanhã sumia primeiro → overbooking).
  const parts = ['select=*', 'order=date.asc,start_time.asc'];
  if (startDate) parts.push(`date=gte.${startDate}`);
  if (endDate) parts.push(`date=lte.${endDate}`);
  if (unitId) parts.push(`unit_id=eq.${encodeURIComponent(unitId)}`);
  if (professionalId) parts.push(`professional_id=eq.${encodeURIComponent(professionalId)}`);
  const rows = await restFetchAll(`${SUPABASE_URL}/rest/v1/appointments?${parts.join('&')}`, { maxRows: Math.min(Number(limit) || 20000, 20000) });
  return rows.map(toApp);
}

// Upsert: cria se id começa com 'apt-' (v2 gerava local), atualiza se UUID válido.
// Aceita também id vazio (cria novo) ou UUID existente (atualiza).
export async function upsertAppointment(apt) {
  const row = { ...toDb(apt), updated_at: new Date().toISOString() };
  // Detecta se é update: id existe E é UUID válido (não 'apt-timestamp')
  const isUpdate = apt.id && apt.id.length === 36 && apt.id.includes('-');
  if (isUpdate) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/appointments?id=eq.${apt.id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao atualizar agendamento: ${await r.text()}`);
    return toApp((await r.json())[0]);
  } else {
    // Cria novo (ignora id 'apt-xxx' legado do v2 — Supabase gera UUID)
    const r = await fetch(`${SUPABASE_URL}/rest/v1/appointments`, {
      method: 'POST',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao criar agendamento: ${await r.text()}`);
    return toApp((await r.json())[0]);
  }
}

export async function deleteAppointment(id) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/appointments?id=eq.${id}`, {
    method: 'DELETE', headers: restHeaders(),
  });
  if (!r.ok) throw new Error(`Falha ao excluir agendamento: ${await r.text()}`);
  return true;
}
